import { filter as _filter, isString } from "lodash";
import type { Emitter } from "mitt";
import mitt from "mitt";
import { defineStore } from "pinia";
import type { Ref } from "vue";
import { ref } from "vue";
import type { BrandResponse } from "../../responses/BrandResponse";
import type { FilterResponse } from "../../responses/FilterResponse";

type Events = {
    updatedFilter: void;
    deletedFilters: void;
};

type InitialFilterList = { [key: string]: InitialFilter };

interface InitialFilter {
    name: string;
    value: string | string[];
    type: string;
}

export type FilterList = { [key: string]: string | string[] };

export const useFilterStore = defineStore("filter", () => {
    const events: Emitter<Events> = mitt<Events>();
    const filter: Ref<string> = ref("");
    const filters: Ref<FilterList> = ref({});

    const displayPrices: Ref<boolean> = ref(true);

    const regularFilters: Ref<FilterResponse[]> = ref([]);
    const propertyFilters: Ref<FilterResponse[]> = ref([]);
    const brandFilters: Ref<BrandResponse[]> = ref([]);

    const setFilterValue = (
        name: string,
        value: string | string[],
        emit: boolean,
    ) => {
        filters.value[name] = value;

        if (emit) {
            events.emit("updatedFilter");
        }
    };

    const getFilter = (name: string) => {
        return filters.value[name];
    };

    const getFilterCount = () => {
        return Object.keys(filters.value).length;
    };

    const hasFilter = (name: string) => {
        return Object.hasOwn(filters.value, name);
    };

    const hasFilterWithValue = (name: string, value: string) => {
        if (hasFilter(name)) {
            const filterValue = getFilter(name);

            if (Array.isArray(filterValue)) {
                return filterValue.includes(value);
            }

            return filterValue === value;
        }

        return false;
    };

    const removeFilterWithValue = (name: string, value: string) => {
        if (hasFilterWithValue(name, value)) {
            let filter = getFilter(name);

            if (Array.isArray(filter)) {
                filter = _filter(filter, (item) => value !== item);
            }

            if (filter.length >= 1) {
                setFilterValue(name, filter, false);
            } else {
                removeFilter(name);
            }
        }
    };

    const addOrRemoveFilter = (name: string, value: string) => {
        if (hasFilterWithValue(name, value)) {
            removeFilterWithValue(name, value);
        } else {
            addFilter(name, value);
        }

        events.emit("updatedFilter");
    };

    const removeFilter = (name: string) => {
        if (hasFilter(name)) {
            delete filters.value[name];
        }
    };

    const resetFilters = () => {
        for (const filterToDelete in filters.value) {
            delete filters.value[filterToDelete];
        }

        events.emit("updatedFilter");
        events.emit("deletedFilters");
    };

    const addFilter = (name: string, value: string) => {
        if (!(typeof value === "string")) {
            console.error(`Only strings allowed to add as filter (${name})`);
            return this;
        }

        let currentValue = filters.value[name];

        if (Array.isArray(currentValue)) {
            if (!Object.hasOwn(currentValue, value)) {
                currentValue.push(value);
            }
        } else {
            if (currentValue !== undefined) {
                currentValue = [currentValue, value];
            } else {
                currentValue = [value];
            }
        }

        setFilterValue(name, currentValue, false);
    };

    const toUrlString = (
        page: number | null = null,
        additionalFilters: { [key: string]: string } | null = null,
        rawFilter: boolean = false,
        pushToHistory: boolean = false,
    ) => {
        let params = filters.value;
        let url = "";

        if (additionalFilters && typeof additionalFilters === "object") {
            params = { ...params, ...additionalFilters };
        }

        if (!rawFilter) {
            params = Object.keys(params).reduce(
                (obj: FilterList, key: string) => {
                    const newObject = obj;
                    newObject[key] = params[key];
                    return newObject;
                },
                {},
            );
        }

        url += Object.keys(params)
            .filter((key: string) => {
                return params[key] !== null && params[key] !== ""; //&& !keyHasDefaultValue(key, params[key]);
            })
            .map((key: string) => {
                let string = "";
                if (Array.isArray(params[key])) {
                    for (let i = 0; i < params[key].length; i++) {
                        const value = params[key][i];
                        string += encodeURIComponent(key) + "[]=" + value;

                        if (params[key].length !== i + 1) {
                            string += "&";
                        }
                    }
                } else if (!(params[key] === null) && params[key].length > 0) {
                    string += encodeURIComponent(key) + "=" + params[key];
                }
                return string;
            })
            .join("&");

        if (url !== "" && !url.includes("?")) {
            url = `?` + url;
        }

        if (page !== null && page > 1) {
            url += (url.includes("?") ? "&" : "?") + `page=${page}`;
        }
        if (pushToHistory) {
            if (url.length > 0) {
                history.pushState({}, "", url);
            } else {
                // Empties the url query params:
                history.pushState({}, "", location.pathname);
            }
        }

        return url;
    };

    const toRawUrlString = (
        page: number | null = null,
        additionalFilters: { [key: string]: string } | null = null,
    ) => {
        return toUrlString(page, additionalFilters, true);
    };

    const setPriceDisplay = (display: boolean) => {
        displayPrices.value = display;
    };

    const setInitialFilters = (filters: InitialFilterList) => {
        for (const key in filters) {
            const filter = filters[key];
            if (Array.isArray(filter.value)) {
                for (const key in filter.value) {
                    const value = filter.value[key];
                    if (value !== undefined) {
                        addFilter(filter.name, value);
                    }
                }
            } else {
                if (filter.value !== undefined) {
                    setFilterValue(filter.name, filter.value, false);
                }
            }
        }
    };

    const setFilterOptions = (filters: FilterList[]) => {
        regularFilters.value = filters["regular"];
        propertyFilters.value = filters["property"];
        brandFilters.value = filters["brands"];
    };

    return {
        events,
        filter,
        filters,
        regularFilters,
        propertyFilters,
        brandFilters,
        displayPrices,
        setFilterValue,
        getFilterCount,
        getFilter,
        hasFilter,
        hasFilterWithValue,
        removeFilterWithValue,
        addOrRemoveFilter,
        removeFilter,
        resetFilters,
        addFilter,
        toUrlString,
        toRawUrlString,
        setInitialFilters,
        setFilterOptions,
        setPriceDisplay,
    };
});
